import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'icon'
  | 'glass'
  | 'glass-frosted'
  | 'glass-icon'
  | 'accent-gloss'
  | 'header-icon';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const SIZE: Record<ButtonSize, string> = {
  // Layout + per-size typography defaults exposed as --btn-* custom props.
  // fontTypographyClasses() reads them as the fallback for the themable
  // font-size / line-height / letter-spacing. These classes MUST stay
  // literal (no `${}`) — Tailwind only generates classes it can see
  // statically in source. type-*-semi was removed so the themable
  // font-size/weight have no cascade rival; we reproduce its exact values
  // via the same Figma tokens here so an un-themed button is identical.
  sm: 'h-7 px-2.5 gap-1 [--btn-font-size:var(--text-caption-1)] [--btn-line-height:var(--leading-caption-1)] [--btn-letter-spacing:normal]',
  md: 'h-9 px-3 gap-1.5 [--btn-font-size:var(--text-subhead)] [--btn-line-height:var(--leading-subhead)] [--btn-letter-spacing:var(--tracking-normal)]',
  lg: 'h-11 px-4 gap-2 [--btn-font-size:var(--text-body)] [--btn-line-height:var(--leading-body)] [--btn-letter-spacing:var(--tracking-body-semi)]',
  // xl — prominent CTAs (e.g. the Accept Buyback button). Headline-size text
  // (--text-headline = 18px) so the label reads large in a tall full-width
  // button. Font-size stays themable via the Button → primaryFontSize knob
  // (the --btn-font-size below is the var() fallback the knob overrides).
  xl: 'h-14 px-5 gap-2 [--btn-font-size:var(--text-headline)] [--btn-line-height:var(--leading-body)] [--btn-letter-spacing:var(--tracking-body-semi)]',
  };

const SIZE_ICON: Record<ButtonSize, string> = {
  sm: 'h-7 w-7',
  md: 'h-9 w-9',
  lg: 'h-12 w-12',
  xl: 'h-14 w-14',
};

const SIZE_GLASS_ICON: Record<ButtonSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-14 w-14',
};

const SIZE_HEADER_ICON: Record<ButtonSize, string> = {
  sm: 'h-9 w-9',
  md: 'h-11 w-11',
  lg: 'h-12 w-12',
  xl: 'h-14 w-14',
};

const BASE =
  'relative inline-flex items-center justify-center select-none cursor-pointer ' +
  'transition-[transform,opacity,box-shadow,filter] duration-150 ease-out ' +
  // Unified hover: drop overall brightness by 8% regardless of fill /
  // variant. Works on any fill colour (light or dark, partner-themed
  // or default), reads consistently, and replaces the per-variant
  // `color-mix` hover that barely moved on light partner themes.
  'hover:[filter:brightness(0.92)] ' +
  'active:scale-[0.97] focus-visible:outline-none ' +
  "focus-visible:[outline:2px_solid_var(--ui-accent)] focus-visible:[outline-offset:2px] " +
  'disabled:opacity-40 disabled:pointer-events-none disabled:cursor-not-allowed';

// Per-variant CSS-var naming: each variant of `Button` owns its own
// `--co-Button-<variant><Slot>` triplet (e.g. `--co-Button-primaryFill`,
// `--co-Button-glass-iconBorder`). The Design Constructor exposes 24 knobs
// (8 variants × 3 slots) so the operator can theme one variant without
// touching the others. Each slot keeps its original fallback chain wrapped
// one level deeper, so an un-overridden variant renders exactly like before.
// Per-variant slots now also include: BorderWidth (themable border weight,
// shorthand keeps style=solid + color), Shadow (box-shadow — covers both
// inset/"inner" and drop shadows in one CSS property), and FontFamily
// (text-bearing variants only). Every new slot wraps its EXACT current
// literal as the `var(..., <literal>)` fallback, so an un-overridden button
// renders pixel-identically to before. Icon-only variants (icon, glass-icon,
// header-icon) get no FontFamily slot.
function variantClasses(variant: ButtonVariant): string {
  if (variant === 'icon') {
    return (
      '[border-radius:var(--co-Button-iconBorderRadius,var(--radius-button))] ' +
      '[background:var(--co-Button-iconFill,var(--ui-bg))] [color:var(--co-Button-iconText,var(--ui-text))] ' +
      '[border:var(--co-Button-iconBorderWidth,0px)_solid_var(--co-Button-iconBorder,transparent)] ' +
      '[box-shadow:var(--co-Button-iconShadow,var(--shadow-outline-ios))]'
    );
  }
  if (variant === 'secondary') {
    return (
      '[border-radius:var(--co-Button-secondaryBorderRadius,var(--radius-pill))] ' +
      '[background:var(--co-Button-secondaryFill,var(--ui-bg))] [color:var(--co-Button-secondaryText,var(--ui-text))] ' +
      '[border:var(--co-Button-secondaryBorderWidth,1px)_solid_var(--co-Button-secondaryBorder,var(--ui-border))] ' +
      '[box-shadow:var(--co-Button-secondaryShadow,var(--shadow-1))] ' +
      '[font-family:var(--co-Button-secondaryFontFamily,inherit)]'
    );
  }
  if (variant === 'ghost') {
    // text-decoration is themable via the `ghostTextDecoration` knob
    // (`none` | `underline`). Default `none` — consumers that need a
    // link-style ghost still opt back in via an `underline` className,
    // which sits later in the cascade and wins.
    return (
      '[border-radius:var(--co-Button-ghostBorderRadius,var(--radius-pill))] ' +
      '[background:var(--co-Button-ghostFill,transparent)] [color:var(--co-Button-ghostText,var(--ui-text))] ' +
      '[border:var(--co-Button-ghostBorderWidth,1px)_solid_var(--co-Button-ghostBorder,transparent)] ' +
      '[box-shadow:var(--co-Button-ghostShadow,none)] ' +
      '[font-family:var(--co-Button-ghostFontFamily,inherit)] ' +
      '[text-decoration:var(--co-Button-ghostTextDecoration,none)]'
    );
  }
  if (variant === 'glass') {
    return (
      '[border-radius:var(--co-Button-glassBorderRadius,var(--radius-pill))] ' +
      '[background:var(--co-Button-glassFill,rgba(255,255,255,0.1))] backdrop-blur ' +
      '[border:var(--co-Button-glassBorderWidth,1px)_solid_var(--co-Button-glassBorder,rgba(255,255,255,0.25))] ' +
      '[color:var(--co-Button-glassText,#fff)] ' +
      '[box-shadow:var(--co-Button-glassShadow,none)] ' +
      '[font-family:var(--co-Button-glassFontFamily,inherit)]'
    );
  }
  if (variant === 'glass-frosted') {
    // Figma 305:26637 — heavy frosted glass used across the My Packs
    // surfaces (header pills, card backgrounds). Two-layer gradient
    // tint, 27px backdrop-blur, soft outer drop-shadow. Lives behind
    // its own `--co-Button-glass-frosted*` knobs so partners can theme
    // it independently of the lighter `glass` variant.
    return (
      '[border-radius:var(--co-Button-glass-frostedBorderRadius,var(--radius-pill))] ' +
      "[background:var(--co-Button-glass-frostedFill,linear-gradient(180deg,rgba(255,255,255,0.20)_0%,rgba(153,153,153,0.20)_100%),linear-gradient(90deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.18)_100%))] " +
      '[backdrop-filter:blur(var(--co-Button-glass-frostedBlur,27px))] ' +
      '[-webkit-backdrop-filter:blur(var(--co-Button-glass-frostedBlur,27px))] ' +
      '[border:var(--co-Button-glass-frostedBorderWidth,1px)_solid_var(--co-Button-glass-frostedBorder,rgba(171,174,179,0.24))] ' +
      '[color:var(--co-Button-glass-frostedText,#fff)] ' +
      '[box-shadow:var(--co-Button-glass-frostedShadow,0_64px_64px_0_rgba(0,0,0,0.05))] ' +
      '[font-family:var(--co-Button-glass-frostedFontFamily,inherit)]'
    );
  }
  if (variant === 'glass-icon') {
    return (
      '[border-radius:var(--co-Button-glass-iconBorderRadius,9999px)] ' +
      '[background:var(--co-Button-glass-iconFill,rgba(255,255,255,0.1))] backdrop-blur ' +
      '[border:var(--co-Button-glass-iconBorderWidth,1px)_solid_var(--co-Button-glass-iconBorder,rgba(255,255,255,0.25))] ' +
      '[color:var(--co-Button-glass-iconText,#fff)] ' +
      '[box-shadow:var(--co-Button-glass-iconShadow,none)]'
    );
  }
  if (variant === 'accent-gloss') {
    // accent-gloss skips the SIZE map (fixed height), so seed the --btn-*
    // typography defaults here (matches the old type-body-semi).
    return (
      'h-[52px] px-4 overflow-hidden ' +
      '[--btn-font-size:var(--text-body)] [--btn-line-height:var(--leading-body)] [--btn-letter-spacing:var(--tracking-body-semi)] ' +
      '[border-radius:var(--co-Button-accent-glossBorderRadius,28px)] ' +
      '[background:var(--co-Button-accent-glossFill,var(--ui-button))] [color:var(--co-Button-accent-glossText,var(--ui-button-text))] ' +
      '[border:var(--co-Button-accent-glossBorderWidth,1px)_solid_var(--co-Button-accent-glossBorder,transparent)] ' +
      '[box-shadow:var(--co-Button-accent-glossShadow,none)] ' +
      '[font-family:var(--co-Button-accent-glossFontFamily,inherit)]'
    );
  }
  if (variant === 'header-icon') {
    return (
      '[border-radius:var(--co-Button-header-iconBorderRadius,9999px)] ' +
      '[background:var(--co-Button-header-iconFill,#fff)] [color:var(--co-Button-header-iconText,var(--color-text-primary))] ' +
      '[border:var(--co-Button-header-iconBorderWidth,1px)_solid_var(--co-Button-header-iconBorder,transparent)] ' +
      '[box-shadow:var(--co-Button-header-iconShadow,0_9px_30px_0_rgba(7,10,18,0.08))]'
    );
  }
  // primary
  return (
    '[border-radius:var(--co-Button-primaryBorderRadius,var(--radius-pill))] ' +
    '[background:var(--co-Button-primaryFill,var(--ui-button))] [color:var(--co-Button-primaryText,var(--ui-button-text))] ' +
    '[border:var(--co-Button-primaryBorderWidth,1px)_solid_var(--co-Button-primaryBorder,transparent)] ' +
    '[box-shadow:var(--co-Button-primaryShadow,var(--shadow-button-gloss))] ' +
    '[font-family:var(--co-Button-primaryFontFamily,inherit)]'
  );
}

// Typography classes for text-bearing variants. font-size + font-weight are
// themable via --co-Button-<variant>{FontSize,FontWeight}; their size-aware
// default + line-height + letter-spacing come from the --btn-* custom props
// seeded by the SIZE map (and the accent-gloss branch).
//
// CRITICAL: every class here is a FULLY-LITERAL string. Tailwind only
// generates CSS for class names it can read statically in source — a class
// assembled via `${interpolation}` is never emitted, so the rule silently
// goes missing and the variable is never consumed. (That was the original
// "font size doesn't work" bug.) Keep these literal; vary only by var name.
function fontTypographyClasses(variant: ButtonVariant): string {
  switch (variant) {
    case 'primary':
      return '[font-size:var(--co-Button-primaryFontSize,var(--btn-font-size))] [font-weight:var(--co-Button-primaryFontWeight,600)] [line-height:var(--btn-line-height)] [letter-spacing:var(--btn-letter-spacing)] [text-transform:var(--co-Button-primaryTextTransform,none)]';
    case 'secondary':
      return '[font-size:var(--co-Button-secondaryFontSize,var(--btn-font-size))] [font-weight:var(--co-Button-secondaryFontWeight,600)] [line-height:var(--btn-line-height)] [letter-spacing:var(--btn-letter-spacing)] [text-transform:var(--co-Button-secondaryTextTransform,none)]';
    case 'ghost':
      return '[font-size:var(--co-Button-ghostFontSize,var(--btn-font-size))] [font-weight:var(--co-Button-ghostFontWeight,600)] [line-height:var(--btn-line-height)] [letter-spacing:var(--btn-letter-spacing)] [text-transform:var(--co-Button-ghostTextTransform,none)]';
    case 'glass':
      return '[font-size:var(--co-Button-glassFontSize,var(--btn-font-size))] [font-weight:var(--co-Button-glassFontWeight,600)] [line-height:var(--btn-line-height)] [letter-spacing:var(--btn-letter-spacing)] [text-transform:var(--co-Button-glassTextTransform,none)]';
    case 'glass-frosted':
      return '[font-size:var(--co-Button-glass-frostedFontSize,var(--btn-font-size))] [font-weight:var(--co-Button-glass-frostedFontWeight,600)] [line-height:var(--btn-line-height)] [letter-spacing:var(--btn-letter-spacing)] [text-transform:var(--co-Button-glass-frostedTextTransform,none)]';
    case 'accent-gloss':
      return '[font-size:var(--co-Button-accent-glossFontSize,var(--btn-font-size))] [font-weight:var(--co-Button-accent-glossFontWeight,600)] [line-height:var(--btn-line-height)] [letter-spacing:var(--btn-letter-spacing)] [text-transform:var(--co-Button-accent-glossTextTransform,none)]';
    default:
      return '';
  }
}

function Spinner({ size }: { size: ButtonSize }) {
  const px = size === 'sm' ? 12 : size === 'md' ? 14 : 18;
  return (
    <span
      aria-hidden
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
      style={{ width: px, height: px }}
    />
  );
}

function GlossOverlay() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 rounded-[inherit]"
      style={{ boxShadow: 'inset 0 -6px 44px rgba(255, 255, 255, 0.79)' }}
    />
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    className = '',
    disabled,
    children,
    ...rest
  },
  ref,
) {
  const isIconOnly =
    variant === 'icon' || variant === 'glass-icon' || variant === 'header-icon';
  const isAccentGloss = variant === 'accent-gloss';

  // accent-gloss has its own fixed height in variantClasses, so skip SIZE map
  let sizeCls = '';
  if (isAccentGloss) {
    sizeCls = '';
  } else if (variant === 'icon') {
    sizeCls = SIZE_ICON[size];
  } else if (variant === 'glass-icon') {
    sizeCls = SIZE_GLASS_ICON[size];
  } else if (variant === 'header-icon') {
    sizeCls = SIZE_HEADER_ICON[size];
  } else {
    sizeCls = SIZE[size];
  }

  const variantCls = variantClasses(variant);

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-co-component="Button"
      data-co-variant={variant}
      className={`${BASE} ${sizeCls} ${variantCls} ${fontTypographyClasses(variant)} ${fullWidth ? 'w-full' : ''} ${className}`.trim()}
      {...rest}
    >
      {isAccentGloss && <GlossOverlay />}
      {loading ? (
        <Spinner size={size} />
      ) : (
        <>
          {leftIcon}
          {!isIconOnly && <span className="whitespace-nowrap">{children}</span>}
          {isIconOnly && children}
          {rightIcon}
        </>
      )}
    </button>
  );
});
